import { create } from "zustand";

import { queryClient } from "@/lib/queryClient";
import { authStoreService, GetStoreResponse } from "@/services/auth";
import { customerTokenService } from "@/services/customer-auth";
import { User } from "@/types";
import { setCookie } from "@/utils/cookie";
import { pinSession } from "@/utils/pinSession";

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: GetStoreResponse | null) => void;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  // Development helper
  setFakeAuth: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,

  setUser: (userData) => {
    const user: User | null = userData
      ? {
          id: userData.id,
          name: userData.name,
          logoImageUrl: userData.logoImageUrl,
          role: "customer",
        }
      : null;

    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    });
  },

  logout: async () => {
    try {
      // Gọi API logout → server xóa HttpOnly cookies
      await authStoreService.logout();
    } catch {
      // Ignore API errors and continue with local cleanup.
    } finally {
      customerTokenService.clearAuth();
      queryClient.clear();
      pinSession.clear();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  fetchMe: async () => {
    set({ isLoading: true });
    try {
      const response = await authStoreService.getMe();

      set({
        user: {
          id: response.id,
          name: response.name,
          logoImageUrl: response.logoImageUrl,
          role: "customer",
        },
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      throw error;
    }
  },

  // Development helper to set fake authentication
  setFakeAuth: () => {
    // Set a fake access token (this won't work with real API calls, but will make isAuthenticated true)
    setCookie("accessToken", "fake-dev-token-for-testing", 1);

    // Set fake user data
    set({
      user: {
        id: "fake-user-id",
        name: "Test User",
        logoImageUrl: "https://via.placeholder.com/150",
        role: "customer",
      },
      isAuthenticated: true,
      isLoading: false,
    });

    console.log(
      "✅ Fake authentication set! You are now logged in as 'Test User'",
    );
    console.log(
      "⚠️ Note: This is for UI testing only. Real API calls may fail.",
    );
  },
}));
